import { useState, useEffect } from "react";
import { Search, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import SearchDialog from "@/components/layout/SearchDialog";
import ToggleArea from "@/components/layout/ToggleArea";
import { useTheme } from "@/contexts/ThemeContext";
import { useLanguage } from "@/contexts/LanguageContext";

const LOGO_DARK = "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/718pfmf4_New%20Company%20Logo.svg";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/pe0mvtqk_RoLink_logo_2025%20%28Vector%20SVG%20for%20Web%29.svg";

const NAV_ITEMS = [
  { key: "home", href: "#hero" },
  { key: "technology", href: "#technology" },
  { key: "science", href: "#science" },
  { key: "applications", href: "#applications" },
  { key: "partners", href: "#partners" },
  { key: "team", href: "#team" },
  { key: "investors", href: "#investment" },
  { key: "news", href: "#news" },
  { key: "contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { theme } = useTheme();
  const { t } = useLanguage();
  const isLight = theme === "light";

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <>
      <nav
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
          scrolled ? "glass py-3" : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
          <a href="#hero" data-testid="nav-logo" className="shrink-0">
            <img src={isLight ? LOGO_LIGHT : LOGO_DARK} alt="RoLink Biotechnology" className="h-10" />
          </a>

          <div className="hidden lg:flex items-center gap-7">
            {NAV_ITEMS.map(item => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-${item.key}`}
                className="text-sm text-gray-400 hover:text-white transition-colors duration-300 tracking-wide"
              >
                {t(`nav.${item.key}`)}
              </a>
            ))}
            <ToggleArea onOpenSearch={() => setSearchOpen(true)} />
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <ToggleArea onOpenSearch={() => setSearchOpen(true)} compact />
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  className={`p-2 ${isLight ? "text-[#0F172A]" : "text-white"}`}
                  data-testid="mobile-menu-btn"
                >
                  <Menu size={24} />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className={`w-72 ${isLight ? "bg-[#FAFAF7] border-[#D1D5DB]" : "bg-[#050505] border-[#1F2937]"}`}
              >
                <div className="mt-8 space-y-1">
                  {NAV_ITEMS.map(item => (
                    <a
                      key={item.href}
                      href={item.href}
                      onClick={() => setMobileOpen(false)}
                      className={`block py-3 px-4 font-heading text-lg transition-colors ${
                        isLight
                          ? "text-[#334155] hover:text-[#0F172A] hover:bg-[#F0F1ED]"
                          : "text-gray-300 hover:text-white hover:bg-[#111111]"
                      }`}
                    >
                      {t(`nav.${item.key}`)}
                    </a>
                  ))}
                </div>
                <button
                  onClick={() => { setSearchOpen(true); setMobileOpen(false); }}
                  className={`mt-4 flex items-center gap-2 px-4 py-3 transition-colors ${
                    isLight ? "text-[#475569] hover:text-[#0F172A]" : "text-gray-400 hover:text-white"
                  }`}
                >
                  <Search size={16} /> {t("nav.search")}
                </button>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}
